import { List } from 'immutable'
import React, { ReactElement } from 'react'

import { AuthParameter, AuthParameters } from '../../api/company-data-integration-setup'
import AvailableDataIntegration from '../../model/availableDataIntegration'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Input from '../elements/input'
import Subcard from '../elements/Subcard'
import Subtitle from '../elements/Subtitle'
import jsBrowserHistory from '../widgets/jsBrowserHistory'
import LoadingOverlay from '../widgets/LoadingOverlay'

type Props = {
  availableDataIntegrations: List<AvailableDataIntegration>

  integrationType: string
  authParameters?: AuthParameters
}

type ConnectionFields = Partial<Record<`parameter-${AuthParameter}`, string>>

export type ParameterConnectionResult = {
  readonly step: 'NeedKey'
  authParameters: AuthParameters
}

function ConnectionForm(
  props: Props & FormComponentProps<ConnectionFields, ParameterConnectionResult>
): ReactElement | null {
  const getIntegration = () => {
    const integrationType = props.integrationType
    return props.availableDataIntegrations.find((integration) => integration.type === integrationType)
  }
  const getIntegrationGuide = (): string => {
    return getIntegration()?.description || ''
  }

  const integration = getIntegration()

  if (!integration || integration.hasAuthURL) {
    return (
      <div style={{ position: 'relative', minHeight: '400px' }}>
        <LoadingOverlay />
      </div>
    )
  }

  const { decorateField } = props

  return (
    <div>
      <div className="title-and-back-btn">
        <Subtitle>{t('company_data_integration.connection_form.title')}</Subtitle>
        <Button onClick={() => jsBrowserHistory.goBack()}>{t('general.back.button')}</Button>
      </div>
      <p dangerouslySetInnerHTML={{ __html: getIntegrationGuide() }} />
      {props.getFormError()}
      <Subcard className="connection-search">
        {integration.parameters.map((parameter) =>
          decorateField(`parameter-${parameter.name}`, {
            title: parameter.displayName,
            placeholder: t('company_data_integration.connection_form.form.parameter.placeholder', {
              parameter: parameter.displayName,
              integration: integration.displayName,
            }),
            validate: (val) =>
              !val
                ? t('company_data_integration.connection_form.form.parameter.required', {
                    parameter: parameter.displayName,
                  })
                : null,
          })(<Input type={parameter.type === 'password' ? 'password' : 'text'} />)
        )}
      </Subcard>
      <Row>
        <Col span={24}>
          <Button
            htmlType="submit"
            size="extra-extra-large"
            type="primary"
            className="gtm-data-integration-continue-from-connection"
          >
            {t('company_data_integration.connection_form.form.submit')}
          </Button>
        </Col>
      </Row>
    </div>
  )
}

export default withValidations<Props, ConnectionFields, ParameterConnectionResult>({
  mapPropsToFields: (props) => {
    const fields: ConnectionFields = {}
    for (const key in props.authParameters) {
      fields[`parameter-${key as AuthParameter}`] = props.authParameters[key as AuthParameter]
    }
    return fields
  },
  onSubmit: (values) => {
    const authParameters: AuthParameters = {}
    for (const key in values) {
      if (!key.match(/^parameter-/)) {
        continue
      }
      authParameters[key.replace(/^parameter-/, '') as AuthParameter] =
        values[key as `parameter-${AuthParameter}`]?.trim()
    }
    return {
      step: 'NeedKey',
      authParameters,
    }
  },
})(ConnectionForm)
